(function () {
  'use strict';

  const PROP_FREE = '_bogo_free';
  const PROP_RULE = '_bogo_rule_type';
  const PROP_PARENT = '_bogo_product_id';
  const NOTIF_SECTION = 'cart-notification-product';

  let reconciling = false;
  let reconcileQueued = false;
  let isInternalBogoOperation = false; // Prevents interceptor recursion

  // --- HELPER: PROPERTY CHECK ---
  const isFreeItem = (item) => {
    if (!item || !item.properties) return false;
    const val = item.properties[PROP_FREE];
    return val === 'true' || val === true;
  };

  // --- HIDE BUY NOW / ACCELERATED CHECKOUT BUTTONS FOR BOGO ITEMS ---
  function hideBuyNowButtons() {
    const selector = '.magic-buy-btn__parent, .shopify-payment-button, .shopify-payment-button__button';
    document.querySelectorAll(selector).forEach(el => el.style.display = 'none');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideBuyNowButtons);
  } else {
    hideBuyNowButtons();
  }

  // --- CART UTILITIES ---
  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());

  function refreshCartNotif(res) {
    const html = res?.sections?.[NOTIF_SECTION];
    const container = document.getElementById(NOTIF_SECTION);
    if (html && container) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      container.innerHTML = wrapper.querySelector('#' + NOTIF_SECTION)?.innerHTML || html;
    }

    // Broadcast cart updates so theme drawers and modals refresh
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: res }, bubbles: true }));
    if (window.Shopify && Shopify.theme && Shopify.theme.cart) {
      document.dispatchEvent(new CustomEvent('page:cart:render', { bubbles: true }));
    }
  }

  function postCart(url, data) {
    isInternalBogoOperation = true; // Mark request as internal
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...data,
        sections: [NOTIF_SECTION],
        sections_url: window.location.pathname
      })
    })
      .then(r => r.json())
      .then(res => {
        refreshCartNotif(res);
        return res;
      })
      .finally(() => {
        isInternalBogoOperation = false;
      });
  }

  // --- RESOLVE CONFIGURATION FROM MODAL, CONFIG MAP, DOM, OR AJAX ---
  function getActiveConfig(triggerEl, addedItem) {
    const productId = addedItem?.product_id;

    // 1. Check window.BOGO_CONFIG_MAP first (Fastest)
    if (productId && window.BOGO_CONFIG_MAP && window.BOGO_CONFIG_MAP[productId]) {
      return Promise.resolve(window.BOGO_CONFIG_MAP[productId]);
    }

    // 2. Check Quick View Modal Scope
    const modalScope = triggerEl && triggerEl.nodeType
      ? triggerEl.closest('.quick-view-modal, quick-view, .modal, [id*="quickview"], [class*="quickview"]')
      : document.querySelector('.quick-view-modal, quick-view, .modal[open], [id*="quickview"]:not([hidden])');

    const scriptInModal = modalScope 
      ? (modalScope.querySelector(`script[data-bogo-config][data-product-id="${productId}"]`) || modalScope.querySelector('script[data-bogo-config]'))
      : null;

    if (scriptInModal) {
      try {
        const config = JSON.parse(scriptInModal.textContent);
        if (!addedItem || String(config.currentProduct?.id) === String(productId)) {
          window.BOGO_CONFIG_MAP = window.BOGO_CONFIG_MAP || {};
          window.BOGO_CONFIG_MAP[productId] = config;
          return Promise.resolve(config);
        }
      } catch (e) {}
    }

    // 3. Check Exact Product Script Tag on Main DOM
    const globalScript = productId 
      ? document.querySelector(`script[data-bogo-config][data-product-id="${productId}"]`)
      : document.querySelector('script[data-bogo-config]');
      
    if (globalScript) {
      try {
        const config = JSON.parse(globalScript.textContent);
        if (!addedItem || String(config.currentProduct?.id) === String(productId)) {
          window.BOGO_CONFIG_MAP = window.BOGO_CONFIG_MAP || {};
          window.BOGO_CONFIG_MAP[productId] = config;
          return Promise.resolve(config);
        }
      } catch (e) {}
    }

    // 4. Fallback: Fetch Product Page JSON via AJAX if missing (Crucial for Quick Views)
    if (addedItem && addedItem.handle) {
      return fetch(`/products/${addedItem.handle}?view=bogo-json`)
        .then(r => r.ok ? r.json() : null)
        .then(config => {
          if (config && config.currentProduct) {
            window.BOGO_CONFIG_MAP = window.BOGO_CONFIG_MAP || {};
            window.BOGO_CONFIG_MAP[productId] = config;
            return config;
          }
          return null;
        })
        .catch(() => null);
    }

    // Default fallback
    if (window.BOGO_CONFIG && (!addedItem || String(window.BOGO_CONFIG.currentProduct?.id) === String(productId))) {
      return Promise.resolve(window.BOGO_CONFIG);
    }

    return Promise.resolve(null);
  }

  // --- VARIANT MATCHING ---
  function findFreeVariant(sourceItem, config) {
    if (!config) return null;
    const isSame = config.type === 'same_product';
    const target = isSame ? config.currentProduct : config.targetProduct;
    if (!target || !target.variants || !target.variants.length) return null;

    if (isSame) {
      return target.variants.find(v => Number(v.id) === Number(sourceItem.variant_id)) || target.variants[0] || null;
    }

    const attrs = config.matchAttributes || [];
    if (!attrs.length) {
      return target.variants.find(v => v.available) || target.variants[0] || null;
    }

    const selectedOptions = {};
    if (sourceItem.options_with_values && sourceItem.options_with_values.length) {
      sourceItem.options_with_values.forEach(opt => {
        selectedOptions[opt.name.toLowerCase().trim()] = opt.value.toLowerCase().trim();
      });
    } else if (sourceItem.variant_options && sourceItem.variant_options.length) {
      sourceItem.variant_options.forEach((val, idx) => {
        const optName = config.currentProduct?.options?.[idx];
        const nameKey = typeof optName === 'string' ? optName : optName?.name;
        if (nameKey) selectedOptions[nameKey.toLowerCase().trim()] = val.toLowerCase().trim();
      });
    }

    const exactMatches = target.variants.filter(variant => {
      return attrs.every(attrName => {
        const cleanAttr = attrName.toLowerCase().trim();
        const wantedValue = selectedOptions[cleanAttr];
        if (!wantedValue) return true;

        const targetOptions = target.options || [];
        const optIdx = targetOptions.findIndex(o => {
          const name = typeof o === 'string' ? o : o.name;
          return name.toLowerCase().trim() === cleanAttr;
        });

        if (optIdx === -1) return true;

        const variantVal = variant['option' + (optIdx + 1)];
        return variantVal && variantVal.toLowerCase().trim() === wantedValue;
      });
    });

    return exactMatches.find(v => v.available)
      || exactMatches[0]
      || target.variants.find(v => v.available)
      || target.variants[0]
      || null;
  }

  // --- RECONCILE ENGINE (SUPPORTS MULTIPLE PRODUCTS IN CART) ---
  async function reconcile(triggerElement) {
    if (reconciling) {
      reconcileQueued = true;
      return;
    }
    reconciling = true;

    try {
      let cart = await getCart();
      if (!cart || !cart.items) return;

      // Extract unique paid products by product_id
      const seenProductIds = new Set();
      const paidProductsInCart = cart.items.filter(item => {
        if (isFreeItem(item) || seenProductIds.has(item.product_id)) return false;
        seenProductIds.add(item.product_id);
        return true;
      });

      // Collect orphan parent IDs (free items whose parent was removed)
      const orphanParentIds = new Set();
      cart.items.forEach(item => {
        if (isFreeItem(item)) {
          const parentId = String(item.properties[PROP_PARENT] || '');
          if (parentId) orphanParentIds.add(parentId);
        }
      });

      paidProductsInCart.forEach(item => orphanParentIds.delete(String(item.product_id)));

      // 1. Scrub orphaned free items
      if (orphanParentIds.size > 0) {
        const updates = {};
        cart.items.forEach(item => {
          if (isFreeItem(item)) {
            const parentId = String(item.properties[PROP_PARENT] || '');
            if (orphanParentIds.has(parentId)) {
              updates[item.key] = 0;
            }
          }
        });
        if (Object.keys(updates).length > 0) {
          await postCart('/cart/update.js', { updates });
        }
      }

      if (!paidProductsInCart.length) return;

      // 2. Process each paid product sequentially with fresh cart state
      for (const sourceItem of paidProductsInCart) {
        const config = await getActiveConfig(triggerElement, sourceItem);
        if (!config || !config.currentProduct) continue;

        // Re-fetch cart state to ensure updated keys/items are fresh
        cart = await getCart();

        const currentProductId = String(config.currentProduct.id);
        const isEnabled = config.enabled !== false;

        let paidQty = 0;
        cart.items.forEach(item => {
          if (String(item.product_id) === currentProductId && !isFreeItem(item)) {
            paidQty += item.quantity;
          }
        });

        const targetFreeQty = isEnabled 
          ? Math.floor(paidQty / (config.buyQty || 1)) * (config.getQty || 1) 
          : 0;

        const freeLines = cart.items.filter(item => {
          const parentId = String(item.properties?.[PROP_PARENT] || '');
          return isFreeItem(item) && parentId === currentProductId;
        });

        const currentFreeQty = freeLines.reduce((sum, item) => sum + item.quantity, 0);

        if (targetFreeQty === currentFreeQty) continue;

        // Case A: Clear all free items
        if (targetFreeQty === 0) {
          if (freeLines.length > 0) {
            const updates = {};
            freeLines.forEach(line => updates[line.key] = 0);
            await postCart('/cart/update.js', { updates });
          }
        } 
        // Case B: Add missing free items
        else if (targetFreeQty > currentFreeQty) {
          const freeVariant = findFreeVariant(sourceItem, config);
          if (freeVariant) {
            const props = {
              [PROP_FREE]: 'true',
              [PROP_PARENT]: currentProductId,
              [PROP_RULE]: config.type
            };
            await postCart('/cart/add.js', {
              items: [{
                id: freeVariant.id,
                quantity: targetFreeQty - currentFreeQty,
                properties: props
              }]
            });
          }
        } 
        // Case C: Multi-line subtraction across free items
        else if (targetFreeQty < currentFreeQty) {
          let diff = currentFreeQty - targetFreeQty;
          const updates = {};
          
          for (let i = freeLines.length - 1; i >= 0 && diff > 0; i--) {
            const line = freeLines[i];
            if (line.quantity <= diff) {
              updates[line.key] = 0;
              diff -= line.quantity;
            } else {
              updates[line.key] = line.quantity - diff;
              diff = 0;
            }
          }
          await postCart('/cart/update.js', { updates });
        }
      }
    } catch (err) {
      console.error('[BOGO Engine Error]', err);
    } finally {
      reconciling = false;
      hideBuyNowButtons();
      if (reconcileQueued) {
        reconcileQueued = false;
        reconcile(triggerElement);
      }
    }
  }

  window.BogoEngine = { reconcile };

  // --- INTERCEPT FETCH REQUESTS ---
  const nativeFetch = window.fetch;
  window.fetch = function () {
    const url = arguments[0] instanceof Request ? arguments[0].url : arguments[0];
    const isCartWrite = typeof url === 'string' && /\/cart\/(add|change|update)/.test(url);

    const res = nativeFetch.apply(this, arguments);

    // Skip interception if request was issued internally by BOGO Engine
    if (isCartWrite && !isInternalBogoOperation) {
      res.then(response => {
        if (response.ok) {
          setTimeout(() => reconcile(), 200);
        }
      });
    }
    return res;
  };

  // --- INTERCEPT XMLHTTPREQUEST (XHR) REQUESTS ---
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._url = url;
    return origOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (!isInternalBogoOperation && typeof this._url === 'string' && /\/cart\/(add|change|update)/.test(this._url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          setTimeout(() => reconcile(), 200);
        }
      });
    }
    return origSend.apply(this, arguments);
  };
})();